import type { ReactNode } from 'react';
import { Check, CheckCheck, Download, FileText, Music, Pin } from 'lucide-react';
import { MessageDeliveryStatus } from '@masters-club/types';
import { cn } from '@/lib/utils';
import { renderSystemMessage } from './system-message';

export interface AdminMessageBubbleAttachment {
  id: number;
  fileId: number;
  type: 'image' | 'video' | 'audio' | 'document';
  url: string;
  name: string;
  size: number;
  mimeType: string;
}

function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) { return `${bytes} B`; }
  if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} KB`; }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentItem({ attachment, isAdmin }: { attachment: AdminMessageBubbleAttachment; isAdmin: boolean }) {
  if (attachment.type === 'video') {
    return (
      <div className="overflow-hidden rounded-lg">
        <video
          src={attachment.url}
          controls
          preload="metadata"
          className="max-h-[240px] w-full rounded-lg"
        >
          <track kind="captions" />
        </video>
      </div>
    );
  }

  if (attachment.type === 'audio') {
    return (
      <div className={cn(
        'flex items-center gap-2 rounded-lg px-3 py-2',
        isAdmin ? 'bg-primary-foreground/10' : 'bg-background/60',
      )}>
        <Music className="size-4 shrink-0 opacity-70" />
        <audio src={attachment.url} controls preload="metadata" className="h-8 w-full">
          <track kind="captions" />
        </audio>
      </div>
    );
  }

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      download={attachment.name}
      className={cn(
        'flex items-center gap-2 rounded-lg px-3 py-2 transition-colors',
        isAdmin
          ? 'bg-primary-foreground/10 hover:bg-primary-foreground/15'
          : 'bg-background/60 hover:bg-background',
      )}
    >
      <FileText className="size-5 shrink-0 opacity-70" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12px] font-medium">{attachment.name}</p>
        <p className="text-[10px] opacity-70">{formatAttachmentSize(attachment.size)}</p>
      </div>
      <Download className="size-4 shrink-0 opacity-70" />
    </a>
  );
}

function MessageAttachments({
  attachments,
  isAdmin,
}: {
  attachments: AdminMessageBubbleAttachment[];
  isAdmin: boolean;
}) {
  const images = attachments.filter((a) => a.type === 'image');
  const others = attachments.filter((a) => a.type !== 'image');

  return (
    <div className="mb-1.5 space-y-1.5">
      {images.length > 0 && (
        <div className={cn('grid gap-1', images.length === 1 ? 'grid-cols-1' : 'grid-cols-2')}>
          {images.map((img) => (
            <a
              key={img.id}
              href={img.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block overflow-hidden rounded-lg"
              title={img.name}
            >
              <img
                src={img.url}
                alt={img.name}
                loading="lazy"
                className="max-h-[240px] w-full object-cover transition-opacity hover:opacity-90"
              />
            </a>
          ))}
        </div>
      )}
      {others.map((attachment) => (
        <AttachmentItem key={attachment.id} attachment={attachment} isAdmin={isAdmin} />
      ))}
    </div>
  );
}

export interface AdminMessageBubbleSender {
  type: 'user' | 'admin';
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email?: string;
}

export interface AdminMessageBubbleReaction {
  emoji: string;
  count: number;
  hasReacted: boolean;
}

export interface AdminMessageBubbleReadBy {
  type: 'user' | 'admin';
  id: number;
  firstName: string | null;
  username: string | null;
  readAt: string;
}

export interface AdminMessageBubbleMessage {
  content: string;
  isSystemMessage: boolean;
  status?: MessageDeliveryStatus;
  isEdited: boolean;
  isDeleted: boolean;
  isPinned?: boolean;
  createdAt: string;
  sender: AdminMessageBubbleSender | null;
  reactions?: AdminMessageBubbleReaction[];
  readBy?: AdminMessageBubbleReadBy[];
  attachments?: AdminMessageBubbleAttachment[];
}

interface AdminMessageBubbleProps {
  message: AdminMessageBubbleMessage;
  actions?: ReactNode;
  badges?: ReactNode;
  className?: string;
  contentClassName?: string;
}

function getSenderDisplayName(sender: AdminMessageBubbleSender | null): string {
  if (!sender) {return 'Система';}
  if (sender.type === 'admin') {
    return sender.username ?? sender.email ?? 'Админ';
  }
  const parts = [sender.firstName, sender.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : (sender.username ?? 'Пользователь');
}

function formatMessageTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 86_400_000);

  if (date >= todayStart) {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }

  if (date >= yesterdayStart) {
    return 'вчера';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
}

function getStatusIcon(message: AdminMessageBubbleMessage, isAdmin: boolean): ReactNode {
  if (!isAdmin) {return null;}

  const readBy = message.readBy ?? [];
  const userReads = readBy.filter((item) => item.type === 'user');
  if (userReads.length > 0) {
    const readTime = formatMessageTime(userReads[0].readAt);
    return (
      <span className="inline-flex items-center gap-0.5 text-blue-500" title={`Прочитано в ${readTime}`}>
        <CheckCheck className="h-3.5 w-3.5" />
      </span>
    );
  }

  if (message.status === MessageDeliveryStatus.READ) {
    return (
      <span className="inline-flex text-blue-500" title="Прочитано">
        <CheckCheck className="h-3.5 w-3.5" />
      </span>
    );
  }

  if (message.status === MessageDeliveryStatus.DELIVERED) {
    return (
      <span className="inline-flex text-muted-foreground" title="Доставлено">
        <CheckCheck className="h-3.5 w-3.5" />
      </span>
    );
  }

  return (
    <span className="inline-flex text-muted-foreground" title="Отправлено">
      <Check className="h-3.5 w-3.5" />
    </span>
  );
}

export function AdminMessageBubble({
  message,
  actions,
  badges,
  className,
  contentClassName,
}: AdminMessageBubbleProps) {
  if (message.isSystemMessage) {
    return (
      <div className={cn('my-2 flex justify-center', className)}>
        <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
          {renderSystemMessage(message.content)}
        </span>
      </div>
    );
  }

  const isAdmin = message.sender?.type === 'admin';
  const reactions = message.reactions ?? [];
  const hasReactions = !message.isDeleted && reactions.length > 0;
  const hasHeader = (!isAdmin && message.sender !== null) ||
    message.isPinned === true ||
    message.isDeleted ||
    badges !== undefined ||
    actions !== undefined;
  const pinnedBadge = message.isPinned ? (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium',
      isAdmin
        ? 'border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground'
        : 'border-border bg-background text-foreground',
    )}>
      <Pin className="h-3 w-3" />
      Закреплено
    </span>
  ) : null;
  const deletedBadge = message.isDeleted ? (
    <span className={cn(
      'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
      isAdmin
        ? 'border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground'
        : 'border-destructive/30 bg-destructive/10 text-destructive',
    )}>
      Удалено
    </span>
  ) : null;

  return (
    <div className={cn('group/message mb-2 flex', isAdmin ? 'justify-end' : 'justify-start', className)}>
      <div className="relative max-w-[78%] min-w-[180px]">
        <div
          className={cn(
            'rounded-2xl px-3.5 py-2',
            isAdmin
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground',
            contentClassName,
          )}
        >
          {hasHeader && (
            <div className="mb-1 flex items-start justify-between gap-3">
              {!isAdmin && message.sender ? (
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  <span className="truncate text-[11px] font-medium text-muted-foreground">
                    {getSenderDisplayName(message.sender)}
                  </span>
                </div>
              ) : (
                <span />
              )}
              <div className="flex shrink-0 items-center gap-1">
                {pinnedBadge}
                {deletedBadge}
                {badges}
                {actions}
              </div>
            </div>
          )}

          {!message.isDeleted && message.attachments && message.attachments.length > 0 && (
            <MessageAttachments attachments={message.attachments} isAdmin={isAdmin} />
          )}

          {message.content && (
            <p className={cn('whitespace-pre-wrap break-words text-sm', message.isDeleted && 'opacity-60')}>
              {message.content}
            </p>
          )}
          {message.isDeleted && (
            <p className="whitespace-pre-wrap break-words text-sm opacity-60">
              {message.content}
            </p>
          )}

          <div className={cn('mt-1 flex items-center gap-1', isAdmin ? 'justify-end' : 'justify-start')}>
            <span
              className={cn(
                'text-[11px]',
                isAdmin ? 'text-primary-foreground/50' : 'text-muted-foreground',
              )}
            >
              {formatMessageTime(message.createdAt)}
              {message.isEdited && ' (ред.)'}
            </span>
            {getStatusIcon(message, isAdmin)}
          </div>
        </div>

        {hasReactions && (
          <div className={cn('mt-1 flex flex-wrap gap-1', isAdmin ? 'justify-end' : 'justify-start')}>
            {reactions.map((reaction) => (
              <span
                key={reaction.emoji}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                  reaction.hasReacted
                    ? 'border-primary/30 bg-primary/10'
                    : 'border-muted-foreground/20 bg-muted/50',
                )}
              >
                <span>{reaction.emoji}</span>
                {reaction.count > 1 && (
                  <span className="text-muted-foreground">{reaction.count}</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
